// Utility functions to construct widget payloads from Redux state

import {createAppSecCollectorItems} from '@/services/team/teamService'

export function getBuildPayload(build: any, componentId?: string, editMode?: boolean) {
  if (!build?.pipelines?.length) return null
  const pipelinesArray = build?.pipelines
    ?.map((b: any) => {
      return typeof b.buildJob === 'object' && b.buildJob !== null ? b.buildJob.fullObject?.id : b.buildJob
    })
    .filter(Boolean)

  const widgetConfigId = null
  const widgetDefaultId = 'build0'
  return {
    name: 'build',
    options: {
      id: widgetConfigId ? widgetConfigId : 'build0',
      buildDurationThreshold:
        build?.buildDurationThreshold !== undefined && build?.buildDurationThreshold !== ''
          ? Number(build.buildDurationThreshold)
          : '',
      consecutiveFailureThreshold: Number(build?.consecutiveFailureThreshold ?? 0),
    },
    componentId,
    collectorItemIds: pipelinesArray,
    ...(editMode ? {widgetDefaultId} : {}),
  }
}

export function getDeployPayload(deploy: any[], componentId?: string, editMode?: boolean) {
  if (!deploy.length || !deploy[0].deployJob) return null
  const deployArray = deploy
    .map((d: any) => {
      if (typeof d.deployJob === 'object' && d.deployJob !== null) {
        return d?.deployJob?.fullObject?.id
      }
      return d.deployJob
    })
    .filter(Boolean)

  const widgetConfigId = null
  const widgetDefaultId = 'deploy0'
  // Build deployInfoList for each pipeline
  const deployInfoList = deploy.map((d: any) => {
    const fullObj = d.deployJob && typeof d.deployJob === 'object' ? d.deployJob.fullObject : undefined
    return {
      collectorItemId: fullObj?.id || d.deployJob,
      pipelineName: fullObj?.options?.applicationName || '',
      enabled: fullObj?.enabled ?? true,
      options: {
        hotfixBranchTemplates: d.deployHotfixBranch,
        productionBranches: d.deployProdBranch,
        productionEnvironmentNames: d.deployProdEnvSuffix,
      },
    }
  })

  return {
    name: 'deploy',
    options: {
      id: widgetConfigId ? widgetConfigId : 'deploy0',
      // Add other deploy options as needed
    },
    componentId,
    collectorItemIds: deployArray,
    deployInfoList,
    ...(editMode ? {widgetDefaultId} : {}),
  }
}

function toPromise<T>(
  obsOrPromise: Promise<T> | {subscribe: (success: (r: T) => void, error?: (e: any) => void) => void},
): Promise<T> {
  if (obsOrPromise && typeof (obsOrPromise as any).then === 'function') {
    return obsOrPromise as Promise<T>
  }
  if (obsOrPromise && typeof (obsOrPromise as any).subscribe === 'function') {
    return new Promise<T>(resolve => {
      ;(obsOrPromise as any).subscribe(resolve, () => resolve(undefined as unknown as T))
    })
  }
  return Promise.resolve(undefined as unknown as T)
}

export async function getRepoPayload(repoForm: any, componentId?: string, editMode?: boolean) {
  if (!repoForm || repoForm.invalid) return null

  const collectors = await import('@/services/team/teamService').then(mod => mod.getCollectorsByType('SCM'))
  const collectorId = collectors && collectors[0]?.id
  if (!collectorId) return null

  const repoData: any[] = []
  let repoId: string | null = null

  for (const repo of repoForm || []) {
    let repoDetails: any
    const branches = repo.branches || []
    if (!repo.repoUrl?.fullObject?.options) {
      repoDetails = createNewRepo(collectorId, repo.repoUrl, branches)
    } else {
      repoDetails = createNewRepo(collectorId, repo.repoUrl?.fullObject?.options?.url, branches)
      repoId = repo?.repoUrl?.fullObject?.id
    }
    if (repoDetails) repoData.push(repoDetails)
  }

  let response: any[] = []

  try {
    response = await toPromise<any[]>(createAppSecCollectorItems(repoData))
  } catch {
    response = []
  }

  const repoConfigId = repoForm?.find((each: any) => each.name === 'repo')?.id
  return {
    name: 'repo',
    componentId,
    collectorItemIds: response.length ? response.map((repoDetail: any) => repoDetail?.id) : repoId ? [repoId] : [],
    options: {
      id: repoConfigId || 'repo0',
      scm: {name: 'AZURE_REPOS', value: 'AZURE_REPOS'},
    },
    ...(editMode ? {widgetDefaultId: 'repo0'} : {}),
  }
}

const createNewRepo = (
  collectorId: string,
  newUrl: string,
  newBranch: string[],
  userID?: string,
  password?: string,
) => ({
  collectorId: collectorId,
  options: {
    url: newUrl,
    branches: newBranch,
    userID,
    password,
  },
  uniqueOptions: {
    url: newUrl,
    branches: newBranch,
  },
})

/**
 * Build the AppSec widget payload by dynamically fetching the AppSec collectorId,
 * creating collector items for each repo, and using the returned collectorItemIds.
 * This function is async and should be awaited.
 *
 * @param appsec - Array of AppSec repo configs from state
 * @param componentId - Dashboard component ID
 * @param editMode - Whether in edit mode
 * @returns The AppSec widget payload (or null if appsec is invalid)
 */
export async function getAppsecPayload(appsec: any[], componentId?: string, editMode?: boolean) {
  if (!appsec.length || appsec.some((a: any) => !a.url)) return null

  // 1. Fetch AppSec collectorId
  const collectors = await import('@/services/team/teamService').then(mod => mod.getAppSecCollectors())
  const collectorId = collectors && collectors[0]?.id
  if (!collectorId) return null

  const repoData = appsec.map((a: any) => ({
    collectorId,
    options: {
      url: typeof a.url === 'object' && a.url !== null ? a.url?.fullObject?.options?.url || a.url?.label : a.url,
      branches: a.branches,
    },
    uniqueOptions: {
      url: typeof a.url === 'object' && a.url !== null ? a.url?.fullObject?.options?.url || a.url?.label : a.url,
    },
  }))

  // 3. Create collector items and get their IDs
  const createdItems = await import('@/services/team/teamService').then(mod => mod.createAppSecCollectorItems(repoData))
  const collectorItemIds = Array.isArray(createdItems) ? createdItems.map((item: any) => item?.id).filter(Boolean) : []

  // 4. Build and return payload
  return {
    name: 'appSecRepo',
    options: {
      id: 'appSecRepo0',
      appSec: {name: 'AppSecFlow', value: 'AppSecFlow'},
    },
    componentId,
    collectorItemIds,
    ...(editMode ? {widgetDefaultId: 'appSecRepo0'} : {}),
  }
}

/**
 * Build the QA widget payload by dynamically fetching the QA collectorId,
 * creating collector items for each QA board/pipeline, and using the returned collectorItemIds.
 * This function is async and should be awaited.
 *
 * @param qa - Array of QA configs from state
 * @param description - Widget description (default: 'qaCollector')
 * @returns The QA widget payload (or null if qa is invalid)
 */
export async function getQAPayload(qa: any[], componentId?: string) {
  try {
    if (!qa.length || qa.some((q: any) => !q.adoBoard)) return null

    // 1. Fetch QA collectorId
    const collectors = await import('@/services/team/teamService').then(mod => mod.getCollectorsByType('QA'))
    const collectorId = collectors && collectors[0]?.id
    if (!collectorId) return null

    const boards: any[] = []
    const pipelines: any[] = []

    qa.forEach((q: any) => {
      let board = null
      let pipeline = null
      // Board extraction
      if (q.adoBoard.fullObject && typeof q.adoBoard.fullObject === 'object') {
        if (
          q.adoBoard.fullObject?.description === 'qaCollector' &&
          q.adoBoard.fullObject?.options?.boards &&
          q.adoBoard.fullObject?.options.boards[0]
        ) {
          const b = q.adoBoard.fullObject?.options.boards[0]
          board = {
            teamId: b.teamId || '',
            teamName: b.teamName || '',
            projectId: b.projectId || '',
            projectName: b.projectName || '',
            org: b.org || '',
          }
        } else {
          board = {
            teamId: q.adoBoard.fullObject?.options?.teamId || '',
            teamName: q.adoBoard.fullObject?.options?.teamName || '',
            projectId: q.adoBoard.fullObject?.options?.projectId || '',
            projectName: q.adoBoard.fullObject?.options?.projectName || '',
            org: q.adoBoard.fullObject?.options?.org || '',
          }
        }
      }
      // Pipeline extraction
      if (q.buildPipeline.fullObject && typeof q.buildPipeline.fullObject === 'object') {
        if (
          q.buildPipeline.fullObject?.description === 'qaCollector' &&
          q.buildPipeline.fullObject?.options?.pipelines &&
          q.buildPipeline.fullObject?.options.pipelines[0]
        ) {
          const p = q.buildPipeline.fullObject?.options.pipelines[0]
          pipeline = {
            jobName: p.jobName || '',
            jobUrl: p.jobUrl || '',
            instanceUrl: p.instanceUrl || '',
          }
        } else {
          pipeline = {
            jobName: q.buildPipeline.fullObject?.options?.jobName || '',
            jobUrl: q.buildPipeline.fullObject?.options?.jobUrl || '',
            instanceUrl: q.buildPipeline.fullObject?.options?.instanceUrl || '',
          }
        }
      }
      if (board) boards.push(board)
      if (pipeline) pipelines.push(pipeline)
    })

    const payloadQA = {
      description: 'qaCollector',
      collectorId,
      options: {
        boards,
        pipelines,
      },
    }

    // 3. Create collector items and get their IDs
    interface QACollectorItem {
      id: string
      options?: {
        boards?: any
        pipelines?: any
      }
    }
    const createdItems = (await saveQACollectorItemObject(payloadQA)) as QACollectorItem | QACollectorItem[]
    const collectorItemIds = Array.isArray(createdItems)
      ? createdItems.map((item: QACollectorItem) => item?.id).filter(Boolean)
      : [createdItems?.id]

    // If createdItems is an array, use the first item's options for boards/pipelines (preserving existing logic)
    const optionsSource = Array.isArray(createdItems) ? createdItems[0] : createdItems

    return {
      name: 'qametrics',
      componentId,
      collectorItemIds: collectorItemIds,
      options: {
        id: 'qametrics0',
        boards: optionsSource?.options?.boards,
        pipelines: optionsSource?.options?.pipelines,
      },
    }
  } catch (error) {
    console.error('getQAPayload error:', error)
    return null
  }
}

const saveQACollectorItemObject = (qaCollectorItemConfig: any) =>
  new Promise(resolve => {
    import('@/services/team/teamService')
      .then(mod => mod.saveQaCollectorItem(qaCollectorItemConfig))
      .then(
        data => {
          if (data != null) {
            resolve(data)
          } else {
            console.error('No QA collector Item available')
            resolve(null)
          }
        },
        error => {
          console.error('Error in saveQACollectorItem ', error)
          resolve(null)
        },
      )
      .catch(error => {
        console.error('Unexpected error in saveQACollectorItemObject', error)
        resolve(null)
      })
  })

export async function getSecurityPayload(securities: any[], componentId?: string, editMode?: boolean) {
  try {
    if (!securities.length || securities.some((s: any) => !s.staticAnalysisJob)) return null

    // Only handle the first security config for now (can be extended to multiple)
    const s = securities[0]
    const widgetConfigId = s?.widgetConfigId || null
    const branch = s?.sonarBranch || null
    // Build the collector item payload for the API
    const formData =
      typeof s.staticAnalysisJob === 'object' && s.staticAnalysisJob !== null
        ? {...s.staticAnalysisJob.fullObject}
        : {id: s.staticAnalysisJob}
    formData.options = formData.options || {}
    formData.options = {
      ...formData.options,
      branch: branch,
    }

    const newPayload = {
      id: formData.id,
      collectorId: formData.collectorId,
      description: formData.description || '',
      options: {
        ...formData.options,
      },
      uniqueOptions: formData.uniqueOptions || {},
    }

    // Create or update the collector item via API
    const createdItem = await saveSecurityCollectorItemObject(newPayload)
    // Ensure id is present and fallback to empty string if not

    const collectorItemId =
      createdItem && typeof createdItem === 'object' && 'id' in createdItem
        ? createdItem.id
        : formData && 'id' in formData
          ? formData.id
          : ''

    return {
      name: 'codeanalysis',
      options: {
        id: widgetConfigId ? widgetConfigId : 'codeanalysis0',
        branch,
      },
      componentId,
      collectorItemIds: collectorItemId ? [collectorItemId] : [],
      ...(editMode ? {widgetDefaultId: 'codeanalysis0'} : {}),
    }
  } catch (error) {
    console.error('getSecurityPayload error:', error)
    return null
  }
}

const saveSecurityCollectorItemObject = async (payload: any) => {
  try {
    const data = await import('@/services/team/teamService').then(mod => mod.saveQaCollectorItem(payload))
    return data
  } catch (error) {
    console.error('Error in saveSecurityCollectorItemObject ', error)
    return null
  }
}

export async function getFeaturePayload(feature: any[], componentId?: string, editMode?: boolean) {
  if (!feature.length || feature.some((f: any) => !f.teamName)) return null
  const f = feature[0]
  const widgetConfigId = null // Set if needed

  const collectorReq = {
    options: {
      relatedBoards: f.relatedBoards,
      teamName: f.teamName?.fullObject?.options?.teamName,
      teamId: f.teamName?.fullObject?.options?.teamId,
      projectName: f.teamName?.fullObject?.options?.projectName,
      projectId: f.teamName?.fullObject?.options?.projectId,
      org: f.teamName?.fullObject?.options?.org || '',
      reusabilityTags: f.reusabilityTags,
      programTags: f.programTags,
      reusabilityBaselineInfo: {
        baselinePeriod: f.reusabilityBaselinePeriod || '',
        globalStoryPoints: Number(f.reusableStoryPoints) || 0,
        totalStoryPoints: Number(f.totalStoryPoints) || 0,
      },
      testAutomationBaselineInfo: {
        baselinePeriod: f.automatedBaselinePeriod || '',
        baselineAutomatedTestCases: Number(f.automatedTestCases) || 0,
        baselineTotalTestCases: Number(f.automationCandidateTestCases) || 0,
        baselineOverallTestCases: Number(f.totalTestCases) || 0,
      },
      thresholds: {
        defectDensity: Number(f.defectDensity),
        onTimeDelivery: Number(f.onTimeDelivery),
        velocity: Number(f.velocity),
        commitmentMet: Number(f.commitmentMet),
        defectLeakage: Number(f.defectLeakage),
        testAutomation: Number(f.testAutomation),
        automationEfficiency: Number(f.automationEfficiency),
        overallAutomationCoverage: Number(f.overallAutomationCoverage),
      },
    },
    collectorId: f.teamName?.fullObject?.collectorId,
    description: f.teamName?.fullObject?.description || 'relatedboard',
    id: f.teamName?.fullObject?.id || undefined,
  }

  // 1. Call API to create/update collector item
  const {saveQaCollectorItem} = await import('@/services/team/teamService')
  let collectorItemId = null
  try {
    const collectorItem = await saveQaCollectorItem(collectorReq)
    collectorItemId = collectorItem?.id || collectorItem?._id
  } catch (err) {
    collectorItemId =
      f.collectorItemId ||
      (typeof f.teamName === 'object' && f.teamName !== null ? f.teamName.fullObject?.id : f.teamName)
  }

  return {
    name: 'feature',
    options: {
      id: widgetConfigId ? widgetConfigId : 'feature0',
      featureTool: f.featureTool,
      relatedBoards: f.relatedBoards,
      teamName: f.teamName?.fullObject?.options?.teamName,
      teamId: f.teamName?.fullObject?.options?.teamId,
      projectName: f.teamName?.fullObject?.options?.projectName,
      projectId: f.teamName?.fullObject?.options?.projectId,
      org: f.teamName?.fullObject?.options?.org || '',
      reusabilityTags: f.reusabilityTags,
      programTags: f.programTags,
      reusabilityBaselineInfo: {
        baselinePeriod: f.reusabilityBaselinePeriod || '',
        globalStoryPoints: Number(f.reusableStoryPoints) || 0,
        totalStoryPoints: Number(f.totalStoryPoints) || 0,
      },
      testAutomationBaselineInfo: {
        baselinePeriod: f.automatedBaselinePeriod || '',
        baselineAutomatedTestCases: Number(f.automatedTestCases) || 0,
        baselineTotalTestCases: Number(f.automationCandidateTestCases) || 0,
        baselineOverallTestCases: Number(f.totalTestCases) || 0,
      },
      thresholds: {
        defectDensity: Number(f.defectDensity),
        onTimeDelivery: Number(f.onTimeDelivery),
        velocity: Number(f.velocity),
        commitmentMet: Number(f.commitmentMet),
        defectLeakage: Number(f.defectLeakage),
        testAutomation: Number(f.testAutomation),
        automationEfficiency: Number(f.automationEfficiency),
        overallAutomationCoverage: Number(f.overallAutomationCoverage),
      },
      estimateMetricType: f.estimateMetricType || 'hours',
      sprintType: f.sprintType || 'issues',
      listType: f.listType || 'scrumkanban',
    },
    componentId,
    collectorItemIds: [
      f.collectorItemId ||
        (typeof f.teamName === 'object' && f.teamName !== null ? f.teamName.fullObject?.id : f.teamName),
    ],
    ...(editMode ? {widgetDefaultId: 'feature0'} : {}),
  }
}
